import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PenLine, CalendarDays, User, Briefcase, X, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const COLORS = ["#2563EB", "#D97706", "#059669", "#DB2777", "#7C3AED", "#0891B2"];
export const FIELD_TYPES = {
  name: { label: "Name", icon: User, w: 0.26, h: 0.03 },
  title: { label: "Title", icon: Briefcase, w: 0.26, h: 0.03 },
  signature: { label: "Signature", icon: PenLine, w: 0.26, h: 0.06 },
  date: { label: "Date signed", icon: CalendarDays, w: 0.18, h: 0.03 },
};
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Drag-and-drop placement of signature / date / name boxes on rendered PDF pages.
export function FieldPlacer({ doc, onSaved, onBack }) {
  const [pages, setPages] = useState(null);
  const [fields, setFields] = useState(doc.fields || []);
  const [rid, setRid] = useState(doc.recipients[0]?.rid);
  const [type, setType] = useState("signature");
  const [busy, setBusy] = useState(false);
  const colorOf = (r) => COLORS[Math.max(0, doc.recipients.findIndex((x) => x.rid === r)) % COLORS.length];

  useEffect(() => { api.get(`/fbsign/${doc.id}/pages`).then((r) => setPages(r.data)).catch(() => toast.error("Could not load pages")); }, [doc.id]);

  function addAt(page, e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const { w, h } = FIELD_TYPES[type];
    const x = clamp((e.clientX - rect.left) / rect.width - w / 2, 0, 1 - w);
    const y = clamp((e.clientY - rect.top) / rect.height - h / 2, 0, 1 - h);
    setFields((fs) => [...fs, { id: Math.random().toString(36).slice(2, 10), rid, type, page, x, y, w, h }]);
  }
  const update = (id, patch) => setFields((fs) => fs.map((f) => (f.id === id ? { ...f, ...patch } : f)));

  async function save(list) {
    const missing = list.length ? doc.recipients.filter((r) => !list.some((f) => f.rid === r.rid && f.type === "signature")) : [];
    if (missing.length) { toast.error(`Add a Signature box for: ${missing.map((m) => m.name).join(", ")}`); return; }
    setBusy(true);
    try { onSaved((await api.patch(`/fbsign/${doc.id}`, { fields: list })).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save fields"); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-3" data-testid="fbsign-field-placer">
      <div className="flex items-center gap-2 flex-wrap sticky top-[44px] bg-white z-10 py-2 border-b border-[var(--line)]">
        <select data-testid="fbsign-field-recipient" value={rid} onChange={(e) => setRid(e.target.value)} className="input-slim text-xs !w-44" style={{ borderColor: colorOf(rid) }}>
          {doc.recipients.map((r) => <option key={r.rid} value={r.rid}>{r.name}</option>)}
        </select>
        {Object.entries(FIELD_TYPES).map(([k, t]) => (
          <button key={k} data-testid={`fbsign-field-type-${k}`} onClick={() => setType(k)}
            className={`flex items-center gap-1 text-xs px-2 py-1 rounded-sm border transition-colors ${type === k ? "border-[var(--navy)] bg-[var(--navy)] text-white" : "border-[var(--line)] hover:border-[var(--navy)]"}`}>
            <t.icon size={12} /> {t.label}
          </button>
        ))}
        <span className="text-[10px] text-[var(--ink-2)] ml-auto">Click a page to drop a box · drag to move · corner to resize</span>
      </div>
      {!pages ? <div className="py-10 flex justify-center"><Loader2 className="animate-spin" /></div> : (
        <div className="space-y-4 bg-[var(--bg-2)] p-3 max-h-[60vh] overflow-y-auto">
          {Array.from({ length: pages.count }).map((_, i) => (
            <div key={i} className="relative mx-auto shadow-sm bg-white select-none cursor-crosshair" style={{ maxWidth: 620, aspectRatio: `${pages.sizes[i].w} / ${pages.sizes[i].h}` }}
              onClick={(e) => { if (e.target === e.currentTarget || e.target.tagName === "IMG") addAt(i, e); }} data-testid={`fbsign-page-${i}`}>
              <img src={`${BACKEND}/api/fbsign/${doc.id}/pages/${i}`} alt={`Page ${i + 1}`} className="w-full h-full block" draggable={false} />
              {fields.filter((f) => f.page === i).map((f) => (
                <FieldBox key={f.id} f={f} color={colorOf(f.rid)} name={doc.recipients.find((r) => r.rid === f.rid)?.name}
                  onChange={(p) => update(f.id, p)} onDelete={() => setFields((fs) => fs.filter((x) => x.id !== f.id))} />
              ))}
              <div className="absolute bottom-1 right-2 text-[9px] text-[var(--ink-2)]">Page {i + 1}</div>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-between items-center">
        <button onClick={onBack} className="btn-ghost text-xs !py-1.5 !px-3">← Back</button>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-[var(--ink-2)]" data-testid="fbsign-field-count">{fields.length} box(es)</span>
          <button data-testid="fbsign-fields-skip" onClick={() => save([])} disabled={busy} className="btn-ghost text-xs !py-1.5 !px-3">Skip (no boxes)</button>
          <button data-testid="fbsign-fields-next" onClick={() => save(fields)} disabled={busy || !fields.length} className="btn-primary text-xs !py-1.5 !px-3">Next: Send</button>
        </div>
      </div>
    </div>
  );
}

function FieldBox({ f, color, name, onChange, onDelete }) {
  const ref = useRef(null);
  const T = FIELD_TYPES[f.type];
  function drag(e, mode) {
    e.preventDefault(); e.stopPropagation();
    const parent = ref.current.parentElement.getBoundingClientRect();
    const start = { x: e.clientX, y: e.clientY, f: { ...f } };
    const move = (ev) => {
      const dx = (ev.clientX - start.x) / parent.width, dy = (ev.clientY - start.y) / parent.height;
      if (mode === "move") onChange({ x: clamp(start.f.x + dx, 0, 1 - f.w), y: clamp(start.f.y + dy, 0, 1 - f.h) });
      else onChange({ w: clamp(start.f.w + dx, 0.04, 1 - start.f.x), h: clamp(start.f.h + dy, 0.015, 1 - start.f.y) });
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }
  return (
    <div ref={ref} data-testid={`fbsign-box-${f.id}`} onPointerDown={(e) => drag(e, "move")} onClick={(e) => e.stopPropagation()}
      className="absolute flex items-center gap-1 px-1 text-[10px] font-medium cursor-move overflow-hidden touch-none"
      style={{ left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%`, border: `1.5px solid ${color}`, background: `${color}22`, color }}
      title={`${T.label} — ${name}`}>
      <T.icon size={11} className="shrink-0" /><span className="truncate">{T.label} · {name}</span>
      <button data-testid={`fbsign-box-delete-${f.id}`} onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="absolute -top-px right-0 w-3.5 h-3.5 flex items-center justify-center bg-white rounded-full" style={{ color }}><X size={9} /></button>
      <span onPointerDown={(e) => drag(e, "resize")} className="absolute right-0 bottom-0 w-2.5 h-2.5 cursor-se-resize" style={{ background: color }} />
    </div>
  );
}
